import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Kind = "certificate" | "prescription" | "referral" | "summary" | "report";

type Issued = {
  id: string;
  kind: Kind;
  reference: string;
  issued_at: string;
  status: "active" | "cancelled";
  cancellation_reason: string;
  doctor_name: string;
  detail: Record<string, unknown>;
};

const KINDS: { key: Kind; label: string; noun: string }[] = [
  { key: "certificate", label: "Certificates", noun: "certificate" },
  { key: "prescription", label: "Prescriptions", noun: "prescription" },
  { key: "referral", label: "Referrals", noun: "referral" },
  { key: "summary", label: "Summaries", noun: "visit summary" },
  { key: "report", label: "Reports", noun: "medical report" },
];

function when(iso: string) {
  return new Date(iso).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function summarise(row: Issued): string {
  const d = row.detail ?? {};
  if (row.kind === "certificate") {
    return `${d.days} day(s), ${d.start_date} to ${d.end_date}`;
  }
  if (row.kind === "prescription") {
    const lines = (d.lines as { medication_text: string }[]) ?? [];
    return lines.map((l) => l.medication_text).join(", ") || "No medicines";
  }
  if (row.kind === "summary") {
    return String(d.what_happened ?? "").split("\n")[0] || "Visit summary";
  }
  if (row.kind === "report") {
    return `${d.addressee} · ${d.purpose}`;
  }
  return `${d.addressee}${d.urgency === "urgent" ? " · urgent" : ""}`;
}

/**
 * What the clinic hands the patient, and the record of having handed it over.
 *
 * Nothing here edits. A certificate with altered dates is a forged
 * certificate, so a mistake is cancelled and reissued, and the cancelled one
 * stays on the list with its reason showing (CLN-F-087, CLN-F-088). The
 * patient may still be holding the paper.
 *
 * Issuing needs the doctor's registration number and the clinic's. When either
 * is missing the server refuses with 422 and names the one to go and fill in,
 * so this component shows that message rather than writing its own.
 */
export function IssuedPanel({ patientId, isDoctor }: { patientId: string; isDoctor: boolean }) {
  const [kind, setKind] = useState<Kind>("certificate");
  const [rows, setRows] = useState<Issued[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  // Regulation gates the certificate and the prescription (PRD 3.4), so the
  // server says whether this clinic may issue each kind today.
  const [issuable, setIssuable] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/patients/{id}/documents/issued", {
      params: { path: { id: patientId }, query: { kind }, header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Issued[]);
    setIssuable(data?.issuable !== false);
  }, [patientId, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const cancel = async (row: Issued) => {
    const reason = window.prompt(
      `Why is ${row.reference} being cancelled? The patient may still be holding it.`,
    );
    if (!reason || !reason.trim()) return;

    const { error: failed } = await api.POST("/portal/documents/issued/{id}/cancel", {
      params: { path: { id: row.id }, query: { kind }, header: clinicHeader() },
      body: { reason: reason.trim() },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  };

  const current = KINDS.find((k) => k.key === kind)!;

  return (
    <section className="issued">
      <div className="issued-head">
        <h2>Issued</h2>
        <div className="issued-tabs">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className={k.key === kind ? "on" : ""}
              onClick={() => {
                setKind(k.key);
                setIssuing(false);
              }}
            >
              {k.label}
            </button>
          ))}
        </div>
        {isDoctor && !issuing && issuable && (
          <button type="button" onClick={() => setIssuing(true)}>
            Issue a {current.noun}
          </button>
        )}
        {isDoctor && !issuable && (
          <span className="muted">
            This clinic does not issue a {current.noun} at present.
          </span>
        )}
      </div>

      <ErrorNote message={error} />

      {issuing && (
        <IssueForm
          patientId={patientId}
          kind={kind}
          onDone={() => {
            setIssuing(false);
            void load();
          }}
          onCancel={() => setIssuing(false)}
        />
      )}

      {loading && <p className="empty">Reading…</p>}

      {!loading && rows.length === 0 && (
        <p className="empty">No {current.noun} issued to this patient.</p>
      )}

      {rows.map((row) => (
        <div key={row.id} className={`issued-row ${row.status === "cancelled" ? "struck" : ""}`}>
          <span className="grow">
            <strong>{row.reference}</strong>
            <span className="muted">
              {" · "}
              {when(row.issued_at)} · {summarise(row)}
            </span>
            {row.status === "cancelled" && (
              <p className="muted issued-cancelled">Cancelled: {row.cancellation_reason}</p>
            )}
          </span>
          <a
            className="btn-like"
            href={`/documents/${kind}/${row.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Open to print
          </a>
          {isDoctor && row.status === "active" && (
            <button type="button" onClick={() => cancel(row)}>
              Cancel
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

function IssueForm({
  patientId,
  kind,
  onDone,
  onCancel,
}: {
  patientId: string;
  kind: Kind;
  onDone: () => void;
  onCancel: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [cert, setCert] = useState({ start_date: today, end_date: today, days: "1", remarks: "" });
  const [lines, setLines] = useState([{ medication_text: "", dosage_text: "", quantity: "", duration_text: "" }]);
  const [notes, setNotes] = useState("");
  const [visitId, setVisitId] = useState("");
  const [visits, setVisits] = useState<{ id: string; label: string }[]>([]);
  const [report, setReport] = useState({ addressee: "", purpose: "", body: "" });
  const [ref, setRef] = useState({
    addressee: "",
    speciality: "",
    reason_text: "",
    clinical_summary: "",
    urgency: "routine",
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A summary names a visit. The list comes from the same endpoint the chart
  // uses, so a doctor picks the consultation rather than typing an id.
  useEffect(() => {
    if (kind !== "summary") return;
    void (async () => {
      const { data } = await api.GET("/portal/patients/{id}/encounters", {
        params: { path: { id: patientId }, header: clinicHeader() },
      });
      setVisits(
        (data?.items ?? []).map((v) => ({
          id: v.id as string,
          label: `${new Date(v.period_start as string).toLocaleDateString("en-MY")} · ${
            (v.reason_text as string) || "Visit"
          }`,
        })),
      );
    })();
  }, [kind, patientId]);

  const detail =
    kind === "summary"
      ? { summarised_encounter_id: visitId }
      : kind === "report"
        ? report
        : kind === "certificate"
      ? { ...cert, days: Number(cert.days) }
          : kind === "prescription"
            ? { lines: lines.filter((l) => l.medication_text.trim()), notes }
            : ref;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const { error: failed } = await api.POST("/portal/patients/{id}/documents/issued", {
      params: { path: { id: patientId }, query: { kind }, header: clinicHeader() },
      body: { detail },
    });
    setSaving(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onDone();
  };

  return (
    <form className="record-form" onSubmit={submit}>
      {kind === "certificate" && (
        <>
          <label>
            From
            <input
              type="date"
              value={cert.start_date}
              onChange={(e) => setCert({ ...cert, start_date: e.target.value })}
              required
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={cert.end_date}
              onChange={(e) => setCert({ ...cert, end_date: e.target.value })}
              required
            />
          </label>
          <label>
            Days <span className="optional">a half day is 0.5</span>
            <input
              type="number"
              step="0.5"
              min="0.5"
              value={cert.days}
              onChange={(e) => setCert({ ...cert, days: e.target.value })}
              required
            />
          </label>
          <label>
            Remarks <span className="optional">optional</span>
            <input value={cert.remarks} onChange={(e) => setCert({ ...cert, remarks: e.target.value })} />
          </label>
        </>
      )}

      {kind === "prescription" && (
        <>
          {lines.map((line, i) => (
            <div className="rx-line" key={i}>
              <label>
                Medicine
                <input
                  value={line.medication_text}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, medication_text: e.target.value };
                    setLines(next);
                  }}
                  placeholder="Paracetamol 500mg"
                />
              </label>
              <label>
                Dosage
                <input
                  value={line.dosage_text}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, dosage_text: e.target.value };
                    setLines(next);
                  }}
                  placeholder="1g QDS PRN"
                />
              </label>
              <label>
                Quantity
                <input
                  value={line.quantity}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, quantity: e.target.value };
                    setLines(next);
                  }}
                  placeholder="20 tabs"
                />
              </label>
            </div>
          ))}
          <button
            type="button"
            className="linklike"
            onClick={() =>
              setLines([...lines, { medication_text: "", dosage_text: "", quantity: "", duration_text: "" }])
            }
          >
            Add another medicine
          </button>
          <label>
            Notes <span className="optional">optional</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </>
      )}

      {kind === "summary" && (
        <>
          <label>
            Visit to summarise
            <select value={visitId} onChange={(e) => setVisitId(e.target.value)} required>
              <option value="">Choose a visit</option>
              {visits.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          {/* The wording that is easy to get wrong, so the form says it. */}
          <p className="hint">
            The summary is written now from what the visit holds, and the words do not change
            afterwards. It carries only what the patient may already read.
          </p>
        </>
      )}

      {kind === "report" && (
        <>
          <label>
            To
            <input
              value={report.addressee}
              onChange={(e) => setReport({ ...report, addressee: e.target.value })}
              placeholder="Human Resources, Axon Sdn Bhd"
              required
            />
          </label>
          <label>
            Purpose
            <input
              value={report.purpose}
              onChange={(e) => setReport({ ...report, purpose: e.target.value })}
              placeholder="Fitness to return to work"
              required
            />
          </label>
          <label>
            The report
            <textarea
              rows={6}
              value={report.body}
              onChange={(e) => setReport({ ...report, body: e.target.value })}
              required
            />
          </label>
        </>
      )}

      {kind === "referral" && (
        <>
          <label>
            To
            <input
              value={ref.addressee}
              onChange={(e) => setRef({ ...ref, addressee: e.target.value })}
              placeholder="Dr Lim, Cardiology, Hospital Kuala Lumpur"
              required
            />
          </label>
          <label>
            Speciality <span className="optional">optional</span>
            <input value={ref.speciality} onChange={(e) => setRef({ ...ref, speciality: e.target.value })} />
          </label>
          <label>
            Reason
            <input
              value={ref.reason_text}
              onChange={(e) => setRef({ ...ref, reason_text: e.target.value })}
              required
            />
          </label>
          <label>
            Clinical summary <span className="optional">optional</span>
            <textarea
              rows={3}
              value={ref.clinical_summary}
              onChange={(e) => setRef({ ...ref, clinical_summary: e.target.value })}
            />
          </label>
          <label>
            Urgency
            <select value={ref.urgency} onChange={(e) => setRef({ ...ref, urgency: e.target.value })}>
              <option value="routine">Routine</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
        </>
      )}

      <ErrorNote message={error} />

      <div className="row-actions">
        <button type="submit" disabled={saving}>
          {saving ? "Issuing…" : "Issue"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
